import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = { singleOpen: Boolean }
  static targets = ['header', 'panel', 'container']

  declare singleOpenValue: boolean
  declare headerTargets: HTMLElement[]
  declare panelTargets: HTMLElement[]
  declare containerTargets: HTMLElement[]

  connect() {
  // initialize ARIA states
    this.headerTargets.forEach((h, i) => {
      // Ensure headers and panels have stable ids so ARIA attributes can reference them
      const panel = this.panelTargets[i]
      if (!h.id) h.id = `faq-header-${i}`
      if (panel && !panel.id) panel.id = `faq-panel-${i}`

      h.setAttribute('role', 'button')
      h.setAttribute('tabindex', '0')
      h.setAttribute('aria-expanded', 'false')
      // Link header <-> panel for accessibility
      if (panel) {
        h.setAttribute('aria-controls', panel.id)
        panel.setAttribute('aria-labelledby', h.id)
        panel.hidden = true
      }

      // ensure container has default styles
      const container = this.containerTargets[i]
      if (container) {
        container.classList.remove('outline-violet-500')
        container.classList.add('outline-slate-200')
      }
      // ensure question title has closed styling by default
      const qInit = h.querySelector('.faq-question') as HTMLElement | null
      if (qInit) {
        // start with closed color
        qInit.classList.remove('text-violet-500')
        qInit.classList.add('text-zinc-900')
      }
    })
  }

  toggle(event: Event) {
    const header = event.currentTarget as HTMLElement
    this.toggleHeader(header)
  }

  keydown(event: KeyboardEvent) {
    const el = event.currentTarget as HTMLElement
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this.toggleHeader(el)
    }
  }

  toggleHeader(header: HTMLElement) {
  const idx = this.headerTargets.indexOf(header)
    if (idx < 0) return

    const panel = this.panelTargets[idx]
    const expanded = header.getAttribute('aria-expanded') === 'true'

    if (this.singleOpenValue) {
      // close others
      this.headerTargets.forEach((h, i) => {
        if (h !== header) {
          h.setAttribute('aria-expanded', 'false')
          const p = this.panelTargets[i]
          if (p) p.hidden = true
          // remove active styles from container
          const cont = this.containerTargets[i]
          if (cont) {
            cont.classList.remove('outline-violet-500')
            cont.classList.add('outline-slate-200')
          }
          const q = h.querySelector('.faq-question')
          if (q) q.classList.remove('text-violet-500')
          const svg = h.querySelector('svg')
          if (svg) svg.classList.remove('rotate-180')
        }
      })
    }

    // toggle this one
    header.setAttribute('aria-expanded', expanded ? 'false' : 'true')
    if (panel) panel.hidden = expanded
    // toggle active styles on container + header children
    const container = this.containerTargets[idx]
    if (container) {
      if (expanded) {
        container.classList.remove('outline-violet-500')
        container.classList.add('outline-slate-200')
      } else {
        container.classList.add('outline-violet-500')
        container.classList.remove('outline-slate-200')
      }
    }

    const q = header.querySelector('.faq-question')
    const svg = header.querySelector('svg')
    if (q) {
      if (expanded) q.classList.remove('text-violet-500')
      else q.classList.add('text-violet-500')
    }
    if (svg) {
      if (expanded) svg.classList.remove('rotate-180')
      else svg.classList.add('rotate-180')
    }

    // If we've just opened this panel, emit a global event for analytics listeners
    const nowOpen = header.getAttribute('aria-expanded') === 'true'
    if (nowOpen) {
      try {
        const evt = new CustomEvent('faq_open', { detail: { index: idx, headerId: header.id }, bubbles: true })
        header.dispatchEvent(evt)
      } catch (e) {
        // swallow errors to avoid breaking UI
      }
    }
  }
}
